import React from 'react';
import {
  Box, Container, Typography, Button, Grid, Chip, Stack,
} from '@mui/material';
import {
  LocalShipping, DirectionsCar, ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import DashboardMockup from './DashboardMockup';

const COURIER_URL = process.env.REACT_APP_COURIER_URL || 'http://localhost:3000';
const DRIVER_URL = process.env.REACT_APP_DRIVER_URL || 'http://localhost:3001';

export default function Hero() {
  return (
    <Box
      component="section"
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #071525 0%, #0d1b2a 40%, #102236 70%, #0a1e35 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 10, md: 0 },
      }}
      className="hero-grid"
    >
      {/* Ambient glow orbs */}
      <Box sx={{
        position: 'absolute', top: '15%', left: '-5%', width: 500, height: 500,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(22,163,74,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', top: '25%', right: '-5%', width: 600, height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(234,88,12,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '10%', left: '30%', width: 400, height: 400,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(30,64,175,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 }, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left: Text */}
          <Grid item xs={12} md={6}>
            <Box sx={{ animation: 'fadeInUp 0.8s ease both' }}>
              <Chip
                label="🚀 Disponível para empresas de estafetas e frotas"
                sx={{
                  mb: 3,
                  background: 'rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.85)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  height: 32,
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.6rem', sm: '3.2rem', md: '3.8rem', lg: '4.2rem' },
                  color: '#fff',
                  mb: 2.5,
                  '& .green': { color: '#4ade80' },
                  '& .orange': { color: '#fb923c' },
                }}
              >
                Gerir cada{' '}
                <Box component="span" className="green">entrega</Box>,{' '}
                <Box component="span" className="orange">motorista</Box>{' '}
                e pagamento
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  color: 'rgba(255,255,255,0.65)',
                  mb: 4,
                  maxWidth: 500,
                  lineHeight: 1.75,
                }}
              >
                A NeoDrive oferece às empresas de estafetas e operadores de frota ferramentas
                dedicadas para acompanhar equipas, automatizar relatórios semanais e processar
                pagamentos SEPA — tudo numa só plataforma.
              </Typography>

              {/* Feature bullets */}
              <Stack spacing={1.2} sx={{ mb: 5 }}>
                {[
                  'Relatórios semanais de remuneração automatizados',
                  'Geração de ficheiros de pagamento SEPA',
                  'Faturas em PDF, WhatsApp e correio eletrónico',
                ].map((feat) => (
                  <Box key={feat} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle sx={{ color: '#4ade80', fontSize: 18 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
                      {feat}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* CTA buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  href={COURIER_URL + '/register'}
                  startIcon={<LocalShipping />}
                  endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                    px: 3.5,
                    py: 1.6,
                    fontSize: '0.95rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #15803d, #166534)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 30px rgba(22,163,74,0.4)',
                    },
                  }}
                >
                  Plataforma de estafetas
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  href={DRIVER_URL + '/register'}
                  startIcon={<DirectionsCar />}
                  endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #ea580c, #c2410c)',
                    px: 3.5,
                    py: 1.6,
                    fontSize: '0.95rem',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #c2410c, #9a3412)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 30px rgba(234,88,12,0.4)',
                    },
                  }}
                >
                  Plataforma de motoristas
                </Button>
              </Stack>

              <Typography sx={{ mt: 2.5, color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                Plano gratuito disponível · Sem cartão de crédito · Configuração em 2 minutos
              </Typography>
            </Box>
          </Grid>

          {/* Right: Dashboard Mockup */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                animation: 'fadeInUp 0.8s ease 0.2s both',
                position: 'relative',
              }}
              className="animate-float"
            >
              <DashboardMockup />
            </Box>
          </Grid>
        </Grid>

        {/* Scroll indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -40,
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            opacity: 0.4,
            animation: 'float 2s ease-in-out infinite',
          }}
        >
          <Box
            sx={{
              width: 1.5,
              height: 40,
              background: 'linear-gradient(to bottom, rgba(255,255,255,0.8), transparent)',
              borderRadius: 2,
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}
